import { ComponentPropsWithRef, ReactNode } from "react";
import styled, { keyframes } from "styled-components";
import {
  DialogProps,
  Overlay as OverlayComp,
  Title as TitleComp,
} from "@radix-ui/react-dialog";
import { zIndex } from "../../styles/constants";
import {
  elevation00,
  minMedium,
  opacity00,
  shade850,
  shade950,
} from "../../styles/tokens";

// TEXT

// STYLES
const modalOpacityIn = keyframes`
  from {
    background-color: transparent;
  }
  to {
    background-color: ${opacity00};
  }
`;

const modalOpacityOut = keyframes`
  from {
    background-color: ${opacity00};
  }
  to {
    background-color: transparent;
  }
`;

const slideInUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const slideOutDown = keyframes`
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(100%);
  }
`;

const slideInDown = keyframes`
  from {
    transform: translateY(-100%);
  }
  to {
    transform: translateY(0);
  }
`;

const slideOutUp = keyframes`
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(-100%);
  }
`;

const slideInLeft = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideOutRight = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
`;

const slideInRight = keyframes`
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideOutLeft = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-100%);
  }
`;

export const Header = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 1rem;

  @media ${minMedium} {
    height: 4.5rem;
  }
`;

export const Title = styled(TitleComp)`
  padding: 1rem 0 0 1rem;

  @media ${minMedium} {
    padding: 1rem 0 0 1.5rem;
  }
`;

export const Container = styled.div<ContainerProps>`
  padding: 1rem;
  overflow: auto;

  box-shadow: ${({ $topShadow }) =>
    $topShadow ? `${elevation00} inset` : "none"};

  border-top: 1px solid
    ${({ $topShadow }) => ($topShadow ? shade850 : "transparent")};

  transition:
    box-shadow 0.3s ease-in-out,
    border-top 0.3s ease-in-out;

  @media ${minMedium} {
    padding: 1.5rem;
  }
`;

export const Content = styled.div<ContentProps>`
  background: ${shade950};
  position: fixed;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 95dvh;
  bottom: 0;

  &[data-state="open"] {
    animation: ${slideInUp} 200ms ease-out;
  }

  &[data-state="closed"] {
    animation: ${slideOutDown} 200ms ease-out;
  }

  @media ${minMedium} {
    max-height: none;
    height: ${({ $origin }) =>
      $origin === "top" || $origin === "bottom" ? "28.125rem" : "100%"};
    width: ${({ $origin }) =>
      $origin === "top" || $origin === "bottom" ? "100%" : "28.125rem"};

    top: ${({ $origin }) => ($origin === "bottom" ? undefined : 0)};
    bottom: ${({ $origin }) => ($origin === "bottom" ? 0 : undefined)};
    left: ${({ $origin }) => ($origin === "left" ? 0 : undefined)};
    right: ${({ $origin }) => ($origin === "left" ? undefined : 0)};

    &[data-state="open"] {
      animation: ${({ $origin }) => {
          switch ($origin) {
            case "top":
              return slideInDown;
            case "bottom":
              return slideInUp;
            case "left":
              return slideInRight;
            case "right":
              return slideInLeft;
          }
        }}
        200ms ease-out;
    }

    &[data-state="closed"] {
      animation: ${({ $origin }) => {
          switch ($origin) {
            case "top":
              return slideOutUp;
            case "bottom":
              return slideOutDown;
            case "left":
              return slideOutLeft;
            case "right":
              return slideOutRight;
          }
        }}
        200ms ease-out;
    }
  }
`;

export const Overlay = styled(OverlayComp)`
  background-color: ${opacity00};
  position: fixed;
  display: flex;
  z-index: ${zIndex.overlay};
  inset: 0;

  &[data-state="open"] {
    animation: ${modalOpacityIn} 200ms ease-out;
  }

  &[data-state="closed"] {
    animation: ${modalOpacityOut} 200ms ease-out;
  }
`;

// DATA

// TYPES
export interface Props
  extends Omit<ComponentPropsWithRef<"div">, "title" | "children">,
    Pick<DialogProps, "defaultOpen" | "open" | "onOpenChange"> {
  /**
   * The interactive element triggering the dialog. Usually a `<Button />`.
   * Can be omitted for when the dialog open state is handled manually.
   */
  trigger?: ReactNode | null;
  /**
   * Determines the side from which the drawer will slide in from in desktop (default: right)
   */
  origin?: "left" | "right" | "top" | "bottom";
  /**
   * The title of the drawer (required for accessibility)
   */
  title: string;
  /**
   * Whether to visually hide the title but still have it read by screen readers
   * @default false
   */
  hideTitle?: boolean;
  /**
   * Whether to include a description for accessibility
   * If true, be sure to include a `<Description />` component from @radix-ui/react-dialog as a child of the drawer.
   *
   * Note: Including a `<Description />` without setting this prop to true will not render the description for screen readers.
   *
   * Note: If you want to hide the description visually but still have it read by screen readers, wrap it inside the
   * `VisuallyHidden` utility from @radix-ui/react-visually-hidden like this `<VisuallyHidden asChild>`
   *
   * @default false
   */
  includeDescription?: boolean;
  /**
   * The dialogs content, can be anything including a `<DialogDescription />`
   */
  children?: ReactNode;
}

type ContainerProps = {
  $topShadow?: boolean;
};

type ContentProps = {
  $origin?: "left" | "right" | "top" | "bottom";
};

// CONSTANTS

// FUNCTIONS

// ASSETS
